import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

// Keeps class spot availability fresh on member schedule pages.
//
// Primary path: ScheduleAvailabilityChannel pushes { id, spots_available,
// cancelled } deltas the moment a booking changes — no polling cost, no lag.
// Fallback path: the original JSON poll, used while the cable is not
// connected (anonymous visitors are rejected by the channel and poll
// forever; signed-in members poll only until the cable connects or while
// it is dropped).
export default class extends Controller {
  static values = {
    url: String,
    interval: { type: Number, default: 30 },
    spotsText: String,
    leftText: String,
    fullText: String,
    cancelledText: String
  }

  connect() {
    this.startPolling()
    this.subscription = consumer.subscriptions.create("ScheduleAvailabilityChannel", {
      received: (data) => this.apply(data.id, data.spots_available, data.cancelled),
      connected: () => {
        this.stopPolling()
        this.refresh() // catch anything missed before/while connecting
      },
      disconnected: () => this.startPolling(),
      rejected: () => this.startPolling()
    })
  }

  disconnect() {
    this.stopPolling()
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  }

  startPolling() {
    if (this.poll || !this.urlValue) return // cable-only surfaces have no poll endpoint
    this.poll = setInterval(() => this.refresh(), this.intervalValue * 1000)
  }

  stopPolling() {
    if (this.poll) {
      clearInterval(this.poll)
      this.poll = null
    }
  }

  async refresh() {
    if (!this.urlValue) return
    const cards = this.element.querySelectorAll("[data-session-id]")
    if (cards.length === 0) return

    const ids = [...new Set([...cards].map(c => c.dataset.sessionId))].join(",")

    try {
      const response = await fetch(`${this.urlValue}?ids=${ids}`)
      if (!response.ok) return

      const data = await response.json()
      data.forEach(s => this.apply(s.id, s.spots_available, s.cancelled))
    } catch (_) {
      // Silently ignore network errors; next poll will retry
    }
  }

  // Update every spot display for one session (a session can appear in
  // several cards, e.g. list + featured sections). Three hook kinds:
  //   [data-availability]        three-state badge (full/low/open classes)
  //   [data-availability-count]  swaps the first number for spots available
  //   [data-availability-taken]  swaps the first number for spots taken
  //   [data-availability-bar]    capacity progress bar width
  // The number-swap hooks keep server-rendered i18n text intact across
  // locales (word order varies, the digits don't).
  apply(sessionId, spots, cancelled = false) {
    const cards = this.element.querySelectorAll(`[data-session-id="${sessionId}"]`)

    cards.forEach(card => {
      const capacity = parseInt(card.dataset.sessionCapacity, 10)
      const taken = Number.isFinite(capacity) ? Math.max(capacity - spots, 0) : null

      card.querySelectorAll("[data-availability-count]").forEach(el => {
        el.textContent = el.textContent.replace(/\d+/, spots)
      })
      if (taken !== null) {
        card.querySelectorAll("[data-availability-taken]").forEach(el => {
          el.textContent = el.textContent.replace(/\d+/, taken)
        })
        card.querySelectorAll("[data-availability-bar]").forEach(el => {
          el.style.width = `${Math.round((taken / capacity) * 100)}%`
        })
      }

      // Book buttons ([data-availability-book]): disable with a "full"
      // label when the class fills while the page is open, restore when a
      // spot frees up. The server still validates every booking attempt.
      card.querySelectorAll("[data-availability-book]").forEach(btn => {
        const full = cancelled || spots <= 0
        if (full && !btn.disabled) {
          btn.dataset.bookLabel = btn.textContent
          btn.textContent = btn.dataset.fullLabel || this.fullTextValue
          btn.disabled = true
          btn.classList.add("opacity-50", "cursor-not-allowed")
        } else if (!full && btn.disabled && btn.dataset.bookLabel) {
          btn.textContent = btn.dataset.bookLabel
          btn.disabled = false
          btn.classList.remove("opacity-50", "cursor-not-allowed")
        }
      })

      const badge = card.querySelector("[data-availability]")
      if (!badge) return

      const compact = badge.hasAttribute("data-compact")

      if (cancelled) {
        badge.className = badge.dataset.fullClass
        badge.textContent = this.cancelledTextValue || this.fullTextValue
        badge.style.display = ""
      } else if (spots <= 0) {
        badge.className = badge.dataset.fullClass
        badge.textContent = this.fullTextValue
        badge.style.display = ""
      } else if (spots <= 3) {
        badge.className = badge.dataset.lowClass
        badge.textContent = compact ? `${spots}` : `${spots} ${this.spotsTextValue} ${this.leftTextValue}`
        badge.style.display = ""
      } else {
        if (compact) {
          badge.style.display = "none"
        } else {
          badge.className = badge.dataset.openClass
          badge.textContent = `${spots} ${this.spotsTextValue}`
          badge.style.display = ""
        }
      }
    })
  }
}
